import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  // Ensure body can scroll - override any App.css restrictions
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    document.body.style.overflow = "auto";
    document.body.style.height = "auto";
    document.documentElement.style.overflow = "auto";
    document.documentElement.style.height = "auto";

    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";
    };
  }, []);

  return (
    <div
      className="bg-gray-50 dark:bg-gray-900"
      style={{ minHeight: "100vh", overflow: "auto" }}
    >
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate("/")}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <svg
                className="h-5 w-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Home
            </button>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              WealthPort AI
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            Last Updated: December 7, 2025
          </p>

          <div className="prose prose-lg dark:prose-invert max-w-none">
            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                1. Introduction
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Welcome to WealthPort ("we," "our," or "us"). We are committed
                to protecting your privacy and ensuring the security of your
                personal information. This Privacy Policy explains how we
                collect, use, disclose, and safeguard your information when you
                use our portfolio tracking and trading intelligence platform.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                By using WealthPort, you agree to the collection and use of
                information in accordance with this policy. If you do not agree
                with our policies and practices, please do not use our service.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                2. Information We Collect
              </h2>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                2.1 Information You Provide
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>
                  <strong>Account Information:</strong> Email address for
                  authentication and account creation
                </li>
                <li>
                  <strong>Portfolio Data:</strong> Trading account details,
                  holdings, transactions, and position information you manually
                  enter or import
                </li>
                <li>
                  <strong>Trading Intelligence:</strong> Notes, screenshots, and
                  other content you upload or create within the platform
                </li>
                <li>
                  <strong>Financial Data:</strong> Transaction history, account
                  balances, and performance metrics derived from your input
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                2.2 Automatically Collected Information
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>
                  <strong>Usage Data:</strong> Information about how you
                  interact with our service, including pages visited, features
                  used, and time spent
                </li>
                <li>
                  <strong>Device Information:</strong> Browser type, operating
                  system, IP address, and device identifiers
                </li>
                <li>
                  <strong>Cookies and Similar Technologies:</strong> We use
                  cookies and local storage to maintain your session and
                  preferences
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                2.3 Third-Party Data
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>
                  <strong>Market Data (Yahoo Finance/yfinance):</strong> We use
                  Yahoo Finance APIs (via yfinance) to retrieve stock prices,
                  market data, and ticker information to update your portfolio
                  values. This service may log your requests and ticker symbols
                  queried.
                </li>
                <li>
                  <strong>Brokerage Integration (Plaid):</strong> If you connect
                  your brokerage account, we use Plaid Inc.'s services to
                  securely access your account holdings, transactions, and
                  balances. Plaid connects to your financial institution on your
                  behalf. By connecting through Plaid, you agree to Plaid's
                  Privacy Policy and Terms of Service.
                </li>
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                3. How We Use Your Information
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>
                  Provide, operate, and maintain our portfolio tracking service
                </li>
                <li>
                  Process your transactions and manage your portfolio data
                </li>
                <li>Calculate performance metrics, P&L, and analytics</li>
                <li>Send you authentication emails (magic links)</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Improve and optimize our service through usage analysis</li>
                <li>
                  Detect, prevent, and address technical issues or security
                  concerns
                </li>
                <li>
                  Comply with legal obligations and enforce our terms of service
                </li>
              </ul>
            </section>

            {/* Data Security */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                4. Data Security
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We take the security of your data seriously and implement
                industry-standard security measures:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>
                  <strong>Encryption:</strong> All data transmitted between your
                  device and our servers is encrypted using TLS/SSL
                </li>
                <li>
                  <strong>Passwordless Authentication:</strong> We use magic
                  link authentication to eliminate password-related
                  vulnerabilities
                </li>
                <li>
                  <strong>Database Security:</strong> Your portfolio data is
                  stored in secure, encrypted databases with access controls
                </li>
                <li>
                  <strong>Regular Security Audits:</strong> We regularly review
                  and update our security practices
                </li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mt-4">
                However, no method of transmission over the Internet or
                electronic storage is 100% secure. While we strive to protect
                your information, we cannot guarantee absolute security.
              </p>
            </section>

            {/* Data Sharing and Disclosure */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                5. Data Sharing and Disclosure
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We do not sell, trade, or rent your personal information to
                third parties. We may share your information only in the
                following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>
                  <strong>Service Providers:</strong> We share data with
                  third-party service providers who assist us in operating our
                  platform, including but not limited to:
                  <ul className="list-circle pl-6 mt-2 space-y-1">
                    <li>Plaid Inc. (brokerage account connections)</li>
                    <li>Yahoo Finance/yfinance (market data)</li>
                    <li>Hosting and infrastructure providers</li>
                    <li>Analytics services</li>
                  </ul>
                </li>
                <li>
                  <strong>Legal Requirements:</strong> We may disclose your
                  information if required by law or in response to valid legal
                  requests
                </li>
                <li>
                  <strong>Business Transfers:</strong> In the event of a merger,
                  acquisition, or sale of assets, your information may be
                  transferred
                </li>
                <li>
                  <strong>With Your Consent:</strong> We may share your
                  information for any other purpose with your explicit consent
                </li>
              </ul>
            </section>

            {/* Your Rights and Choices */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                6. Your Rights and Choices
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You have the following rights regarding your personal
                information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>
                  <strong>Access:</strong> Request access to the personal
                  information we hold about you
                </li>
                <li>
                  <strong>Correction:</strong> Request correction of inaccurate
                  or incomplete data
                </li>
                <li>
                  <strong>Deletion:</strong> Request deletion of your account
                  and associated data
                </li>
                <li>
                  <strong>Export:</strong> Export your portfolio data in CSV
                  format at any time
                </li>
                <li>
                  <strong>Opt-Out:</strong> Opt out of non-essential
                  communications
                </li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mt-4">
                To exercise any of these rights, please contact us using the
                information provided in the Contact section below.
              </p>
            </section>

            {/* Data Retention */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                7. Data Retention
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                We retain your personal information for as long as your account
                is active or as needed to provide you services. If you request
                account deletion, we will delete or anonymize your data within
                30 days, except where we are required to retain it for legal or
                regulatory purposes.
              </p>
            </section>

            {/* Children's Privacy */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                8. Children's Privacy
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                WealthPort is not intended for use by individuals under the age
                of 18. We do not knowingly collect personal information from
                children. If you believe we have collected information from a
                child, please contact us immediately.
              </p>
            </section>

            {/* International Users */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                9. International Users
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                WealthPort is operated in the United States. If you are
                accessing our service from outside the United States, please be
                aware that your information may be transferred to, stored, and
                processed in the United States where our servers are located. By
                using our service, you consent to this transfer.
              </p>
            </section>

            {/* Changes to Privacy Policy */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                10. Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                We may update this Privacy Policy from time to time. We will
                notify you of any changes by posting the new Privacy Policy on
                this page and updating the "Last Updated" date. We encourage you
                to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                11. Contact Us
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                If you have any questions about this Privacy Policy or our data
                practices, please contact us:
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>WealthPort</strong>
                  <br />
                  Email: privacy@wealthport.ai
                </p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                12. Your Acknowledgment
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                By using WealthPort, you acknowledge that you have read and
                understood this Privacy Policy and agree to its terms.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                Please also review our{" "}
                <button
                  onClick={() => navigate("/terms")}
                  className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                >
                  Terms of Service
                </button>
                .
              </p>
            </section>
          </div>

          {/* Action Buttons */}
          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate("/")}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Back to Home
            </button>
            <button
              onClick={() => navigate("/login")}
              className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Get Started
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
